import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, History } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useComunicadosSistemaVigentes } from "@/hooks/comunicados/useComunicadosSistemaVigentes";
import { ComunicadoSinoItem } from "./comunicado-superficies";
import { ComunicadoSistemaDialog } from "./ComunicadoSistemaDialog";
import { ComunicadoSistemaVigenteDTO } from "@/types/comunicado-sistema.type";

/** Megafone no Header: índice de tudo que está vigente agora. */
export function ComunicadoSistemaSino() {
  const navigate = useNavigate();
  const { vigentes, total } = useComunicadosSistemaVigentes();
  const [isOpen, setIsOpen] = useState(false);
  const [aberto, setAberto] = useState<ComunicadoSistemaVigenteDTO | null>(null);

  // Sem vigentes o megafone continua no Header (sem badge): é por ele que se
  // chega ao histórico, inclusive quando não há nada no ar agora.
  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger
          className={buttonVariants({
            variant: "ghost",
            size: "icon",
            className: cn(
              "relative h-9 w-9 rounded-full transition-all hover:bg-muted",
              isOpen ? "bg-muted text-primary" : "text-muted-foreground",
            ),
          })}
        >
          <Megaphone className="h-5 w-5" />
          {total > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground ring-2 ring-background">
              {total > 99 ? "99+" : total}
            </span>
          )}
          <span className="sr-only">Comunicados</span>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-[calc(100vw-4rem)] overflow-hidden rounded-xl border-border/50 p-0 sm:w-96"
        >
          <div className="flex items-center justify-between border-b border-border/50 bg-card px-4 py-3">
            <h4 className="text-sm font-semibold text-foreground">Comunicados</h4>
            <span className="text-xs text-muted-foreground">
              {total} {total === 1 ? "vigente" : "vigentes"}
            </span>
          </div>

          <div className="custom-scrollbar flex max-h-[50vh] flex-col divide-y divide-border/40 overflow-y-auto sm:max-h-[350px]">
            {total === 0 && (
              <p className="px-4 py-6 text-center text-xs text-muted-foreground">
                Nenhum comunicado no ar agora.
              </p>
            )}

            {vigentes.map((c) => (
              <ComunicadoSinoItem
                key={c.id}
                comunicado={c}
                onClick={() => {
                  setAberto(c);
                  setIsOpen(false);
                }}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              navigate("/comunicados");
            }}
            className="flex w-full items-center justify-center gap-1.5 border-t border-border/50 bg-card px-4 py-2.5 text-xs font-medium text-primary transition-colors hover:bg-muted/40"
          >
            <History className="h-3.5 w-3.5" />
            Ver todos os comunicados
          </button>
        </PopoverContent>
      </Popover>

      <ComunicadoSistemaDialog comunicado={aberto} onOpenChange={(o) => !o && setAberto(null)} />
    </>
  );
}
